import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BodyWrapper, Header, Loader, MainWrapper, Message } from '@components';
import {
  authorsQuery,
  blogCategoriesQuery,
  postMutation,
  siteQuery,
} from '@queries';
import { ChevronRightIcon, SearchIcon } from '@svgs';
import PostForm from '../components/PostForm/PostForm.jsx';
import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';
import { useDatamodel } from '@hooks';

export const AddPost = () => {
  const { t, i18n } = useTranslation();
  const { siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState(null);
  const errorMessageRef = useRef(null);

  // scroll to error message when it's set
  useEffect(() => {
    if (errorMessage && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [errorMessage]);
  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // get author
  const { data: authorsData, isPending: authorsIsPending } = useQuery(
    authorsQuery({
      limit: 100,
      order_by: 'name',
      site_id: siteID,
    }),
  );

  // get categories
  const { data: blogCategoriesData, isPending: blogCategoriesIsPending } =
    useQuery(
      blogCategoriesQuery({
        limit: 100,
        site_id: siteID,
      }),
    );

  const {
    datamodelData,
    isPending: isCustomBlogsPending,
    relatedDatamodels,
    relatedDatamodelsReady,
  } = useDatamodel('blog_custom_fields');

  // console.log("datamodelData", datamodelData)
  // save post
  const mutation = useMutation(
    postMutation(null, {
      // on trigger
      onMutate: (data) => {
        setErrorMessage(null);
      },
      onSuccess: (data) => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['posts', 'list'] });
        // navigate to the edit view
        navigate(`/dashboard/sites/${siteID}/blogs/posts/${data.id}`);
      },
      onError: (error) => {
        const errorsList = errorsFromMutate(error);
        const allError = errorsList.find(({ field }) => field === '__all__');
        if (!!allError) {
          setErrorMessage(allError.error.message);
        }
      },
    }),
  );

  const defaultPost = {
    published: true,
    site_id: siteID,
  };

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/blogs`}>
                {t('Blog posts')}
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <span>Add</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            {errorMessage && (
              <div ref={errorMessageRef}>
                <Message
                  closable={true}
                  messageStyle="error"
                  title={'Something Went Wrong'}
                  isFormMessage={false}
                  message={errorMessage}
                />
              </div>
            )}
            {(authorsIsPending ||
              blogCategoriesIsPending ||
              isCustomBlogsPending ||
              !relatedDatamodelsReady) && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {authorsData && blogCategoriesData && !isCustomBlogsPending && (
              <PostForm
                postData={defaultPost}
                authorsData={authorsData}
                categoriesData={blogCategoriesData}
                datamodelData={datamodelData}
                relatedDatamodels={relatedDatamodels}
                mutation={mutation}
                disabled={mutation.isPending}
                siteData={siteData}
                onCancel={() => {
                  navigate(`/dashboard/sites/${siteID}/blogs`);
                }}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
